import { Link } from 'react-router-dom';
import useSeo from '@/hooks/useSeo';
import { testimonials, site, stats } from '@/mocks/site';
import { dimensions } from '@/mocks/dimensions';
import Breadcrumbs from '@/components/feature/Breadcrumbs';

const principles = [
  {
    icon: 'ri-focus-3-line',
    title: '先对齐素材，再谈解读',
    desc: '任何拆解都从原始素材开始。版本不同的录屏、被剪掉的时间、被压缩的画质，都要先标注清楚，再进入推理。',
  },
  {
    icon: 'ri-scales-3-line',
    title: '区分「已确认」与「待确认」',
    desc: '能被反复核对的内容写进正文，只能推测的部分单独标注。两者不混写，避免把猜想当成结论。',
  },
  {
    icon: 'ri-repeat-line',
    title: '可复现才算线索',
    desc: '我们只把能按时间检索、能被第三方复核的片段当作线索。无法复现的内容，即使传播很广，也不会进入档案。',
  },
  {
    icon: 'ri-time-line',
    title: '热度过去之后仍继续跟进',
    desc: '事件降温不代表脉络走完。档案会保留旧节点并追加新节点，让你能看到走势是怎么变化的。',
  },
];

const workflow = [
  { step: '01', title: '收集原始素材', desc: '锁定时间戳、设备来源与首次出现渠道，剔除二次剪辑版本。' },
  { step: '02', title: '按小时排时间线', desc: '把动作而不是说法排进去，标出频率异常的时间窗。' },
  { step: '03', title: '找多余的元素', desc: '把当时看起来多余、事后被验证的细节单独拎出来。' },
  { step: '04', title: '叠加名单找关联', desc: '把不同事件的参与方画成图，只保留重复出现的节点。' },
];

export default function About() {
  useSeo({
    title: '关于我们 - 爆料看点｜内容原则与拆解方法',
    description:
      '了解爆料看点的定位、内容原则与拆解方法：从爆料背后细节、被忽略的伏笔到时间线关键节点与事件隐藏关联，我们如何把零散线索整理成可核对的脉络。',
    keywords: '关于爆料看点,爆料背后细节,时间线关键节点,事件隐藏关联,内容原则',
    path: '/about',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于爆料看点',
        description: '爆料看点的定位、内容原则与拆解方法说明。',
        inLanguage: 'zh-CN',
        isPartOf: { '@type': 'WebSite', name: site.name, url: 'https://baoliaokandian.site/' },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: site.name,
        url: 'https://baoliaokandian.site/',
        email: site.email,
        description: site.description,
        foundingDate: site.since,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://baoliaokandian.site/' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://baoliaokandian.site/about' },
        ],
      },
    ],
  });

  return (
    <>
      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1200px] px-4 pt-8 md:px-8">
          <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
        </div>
        <div className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-8 md:py-16">
          <span className="font-label text-xs tracking-[0.3em] text-primary-600">ABOUT / 关于我们</span>
          <h1 className="mt-3 max-w-3xl font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
            我们只做一件事：把爆料拆开，摆平
          </h1>
          <p className="mt-6 max-w-2xl font-body text-base leading-relaxed text-foreground-700">
            {site.name} 成立于 {site.since} 年，是一个专注事件拆解的内容站。我们不做热闹的搬运，也不急着下结论，
            而是把每一件事放回它自己的时间轴上，从爆料背后细节、被忽略的伏笔、时间线关键节点和事件隐藏关联四个角度，
            把线索整理成一条你可以顺着看下去的脉络。
          </p>
          <p className="mt-4 max-w-2xl font-body text-base leading-relaxed text-foreground-700">
            {site.tagline}——这是我们给自己定的一句话，也是每一篇档案的起点。
          </p>
        </div>
      </section>

      <section className="w-full border-b border-background-200 bg-background-50">
        <div className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-8 md:py-16">
          <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
            {stats.map((stat) => (
              <div key={stat.label} className="border-l-2 border-primary-500 pl-4">
                <p className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{stat.value}</p>
                <p className="mt-1 font-label text-sm text-foreground-800">{stat.label}</p>
                <p className="mt-1 font-label text-xs text-foreground-500">{stat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-8 md:py-16">
          <span className="font-label text-xs tracking-[0.3em] text-secondary-600">内容原则 / PRINCIPLES</span>
          <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">我们坚持的四条底线</h2>
          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2">
            {principles.map((item) => (
              <div key={item.title} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="flex h-11 w-11 flex-none items-center justify-center rounded-md bg-secondary-500 text-background-50">
                  <i className={`${item.icon} text-lg`} />
                </span>
                <div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 font-body text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full border-b border-background-200 bg-background-50">
        <div className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-8 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <span className="font-label text-xs tracking-[0.3em] text-primary-600">拆解方法 / METHOD</span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                一篇档案是怎么做出来的
              </h2>
              <p className="mt-5 font-body text-sm leading-relaxed text-foreground-600">
                四步流程固定不变，每一步都要留下可以复查的记录。
              </p>
            </div>
            <ol className="lg:col-span-8">
              {workflow.map((item) => (
                <li key={item.step} className="flex gap-5 border-t border-background-200 py-6 first:border-t-0 first:pt-0">
                  <span className="font-heading text-2xl font-bold text-accent-500">{item.step}</span>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-foreground-950">{item.title}</h3>
                    <p className="mt-2 font-body text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-8 md:py-16">
          <span className="font-label text-xs tracking-[0.3em] text-accent-600">维度说明 / DIMENSIONS</span>
          <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">四个维度，四种看法</h2>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {dimensions.map((dimension) => (
              <Link
                key={dimension.id}
                to={`/dossiers?dimension=${dimension.id}`}
                className="group flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-6 transition-colors hover:border-primary-300"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-background-200 text-foreground-900 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                  <i className={`${dimension.icon} text-lg`} />
                </span>
                <h3 className="mt-5 font-heading text-base font-semibold text-foreground-950">{dimension.name}</h3>
                <p className="mt-2 font-body text-sm leading-relaxed text-foreground-600">{dimension.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full border-b border-background-200 bg-background-50">
        <div className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-8 md:py-16">
          <span className="font-label text-xs tracking-[0.3em] text-secondary-600">用户口碑 / VOICES</span>
          <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">读者怎么看我们的方法</h2>
          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2">
            {testimonials.map((item) => (
              <figure key={item.name} className="rounded-lg border border-background-200 bg-background-100 p-6">
                <blockquote className="font-body text-sm leading-relaxed text-foreground-700">「{item.text}」</blockquote>
                <figcaption className="mt-4 flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 font-heading text-sm font-bold text-secondary-800">
                    {item.name.slice(0, 1)}
                  </span>
                  <span>
                    <span className="block font-heading text-sm font-semibold text-foreground-950">{item.name}</span>
                    <span className="block font-label text-xs text-foreground-500">{item.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100">
        <div className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-8 md:py-16">
          <div className="grid grid-cols-1 gap-8 rounded-lg border border-background-200 bg-background-50 p-8 md:p-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">想聊一聊，或者提供线索</h2>
              <p className="mt-3 font-body text-sm leading-relaxed text-foreground-600">
                投稿、纠错、补充时间线都可以。请附上素材出现的时间与来源，方便我们做交叉核对。
              </p>
              <div className="mt-5 space-y-2 font-label text-sm text-foreground-700">
                <p>
                  <i className="ri-mail-line mr-2 text-primary-500" />
                  投稿邮箱：
                  <a href={`mailto:${site.email}`} className="text-primary-600 hover:text-primary-700" rel="nofollow">
                    {site.email}
                  </a>
                </p>
                <p>
                  <i className="ri-time-line mr-2 text-primary-500" />
                  响应时间：{site.workingHours}
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-3 lg:col-span-5 lg:justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-send-plane-line" />
                前往联系页面
              </Link>
              <Link
                to="/dossiers"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-6 py-3 font-label text-sm text-foreground-800 transition-colors hover:border-foreground-800"
              >
                <i className="ri-book-open-line" />
                浏览拆解档案
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}